import React, { useCallback, useEffect, useRef, useState } from 'react';
import Icon from 'components/styleguide/icon/Icon';
import Button from 'components/styleguide/button/Button';
import { useAutoSave } from 'components/styleguide/hooks/useAutoSave';

const styles = require('./ImageUpload.module.scss');

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const MIN_DIMENSION = 280;
const ALLOWED_TYPES = ['image/jpeg', 'image/png'];

export interface ImageUploadProps {
  alt?: string;
  autoSaveUrl?: string;
  imageUrl?: string;
  name?: string;
  onChange?: (file: File) => void;
  onError?: (error: string) => void;
  readOnly?: boolean;
}

const validateDimensions = (file: File): Promise<string | null> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      if (img.width < MIN_DIMENSION || img.height < MIN_DIMENSION) {
        resolve(`Image must be at least ${MIN_DIMENSION}x${MIN_DIMENSION}px`);
      } else {
        resolve(null);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      resolve('Could not read image file');
    };
    img.src = URL.createObjectURL(file);
  });
};

const ImageUpload: React.FC<ImageUploadProps> = ({
  alt = 'Uploaded image',
  autoSaveUrl,
  imageUrl,
  name,
  onChange,
  onError,
  readOnly,
}) => {
  const { autoSaveError, save } = useAutoSave({
    url: autoSaveUrl || '',
    name: name || '',
    debounceMs: 0,
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { if (autoSaveError) setError(autoSaveError); }, [autoSaveError]);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    if (!ALLOWED_TYPES.includes(selected.type)) {
      const msg = 'Only JPG or PNG files are allowed';
      setError(msg);
      onError?.(msg);
      return;
    }

    if (selected.size > MAX_FILE_SIZE) {
      const msg = 'File size must be less than 5 MB';
      setError(msg);
      onError?.(msg);
      return;
    }

    const dimensionError = await validateDimensions(selected);
    if (dimensionError) {
      setError(dimensionError);
      onError?.(dimensionError);
      return;
    }

    setError(null);
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(selected);
    });
    if (autoSaveUrl) save(selected);
    onChange?.(selected);
  }, [onChange, onError]);

  const displayUrl = previewUrl || imageUrl;
  const hasImage = !!displayUrl;
  const buttonLabel = hasImage ? 'Replace image' : 'Upload image';
  const buttonIcon = hasImage ? 'repeat' : 'upload';

  return (
    <div className={styles.container}>
      {hasImage ? (
        <img src={displayUrl} alt={alt} className={styles.image} />
      ) : (
        <div className={styles.placeholder} aria-hidden="true">
          <Icon name="camera" size="3xl" color="disable" />
        </div>
      )}
      {!readOnly && (
        <>
          <div className={styles.info}>
            <div className={styles.actions}>
              <Button
                type="terceary"
                context="company"
                size="S"
                iconLeft={buttonIcon}
                onClick={handleClick}
                aria-label={buttonLabel}
              >
                {buttonLabel}
              </Button>
            </div>
            {error ? (
              <p className={styles.error} role="alert">{error}</p>
            ) : (
              <p className={styles.constraints}>
                Min. dimensions: 280x280px<br />
                Max. file size: 5 MB<br />
                JPG or PNG only
              </p>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            name={name}
            accept="image/jpeg,image/png"
            onChange={handleFileChange}
            className={styles.fileInput}
            aria-label="Choose image to upload"
            tabIndex={-1}
          />
        </>
      )}
    </div>
  );
};

export default ImageUpload;
